// Gmail API client helpers for the GmailSearchPanel + the Gmail
// thread import flow. Thin module of `fetch` wrappers: we hit the
// Gmail REST API directly with the operator's access token; nothing
// goes through the InRange backend.
//
// Threads are imported as first-class STRUCTURED files: a JSON
// manifest (`GmailThreadStructured`) carrying per-message metadata +
// bodies + an attachments manifest. The import orchestration in
// `hooks/useGmailThreadImport.ts` PUTs that JSON (content_type
// "application/json", kind "email_thread") and pulls each attachment
// into S3 alongside it.

export const GMAIL_MAX_RESULTS = 25;
export const GMAIL_API_BASE = "https://gmail.googleapis.com/gmail/v1/users/me";

// Gmail enforces a per-mailbox CONCURRENT-request limit of 50 in-flight,
// shared across ALL clients hitting that mailbox - including our backend
// sync/poll on the same Google account. (This is separate from the
// 6,000-units/min rate limit.) Exceeding it returns 429 "Too many concurrent
// requests for user". We fan browser->Gmail calls out at a small fraction of
// that ceiling so an interactive search can't collide with the backend's
// in-flight requests (or a 401-retry re-firing a batch) and trip it.
export const GMAIL_MAX_CONCURRENCY = 5;

// Run `fn` over `items` with at most `limit` promises in flight at once,
// preserving result order. Used to keep the browser's Gmail fan-out under the
// per-mailbox concurrency ceiling. Rejects on the first `fn` rejection, like
// Promise.all (callers wrap this in their 401-retry).
export const mapWithConcurrency = async <T, R>(
  items: readonly T[],
  limit: number,
  fn: (item: T, index: number) => Promise<R>,
): Promise<R[]> => {
  const results = new Array<R>(items.length);
  let cursor = 0;
  const worker = async (): Promise<void> => {
    for (let i = cursor++; i < items.length; i = cursor++) {
      results[i] = await fn(items[i], i);
    }
  };
  const poolSize = Math.max(1, Math.min(limit, items.length));
  await Promise.all(Array.from({ length: poolSize }, () => worker()));
  return results;
};

// Attachments larger than this are NOT pulled - the manifest records
// them as status="skipped". Keep in lock-step with the backend's
// attachment-size guard (_ATTACHMENT_BYTES_CAP in
// src/inrange_agents/adaptors/gmail_client.py). 100 MB: a 2026-06 prod
// scan found the largest attachment at 33 MB and none over 50 MB.
export const GMAIL_ATTACHMENT_MAX_BYTES = 100 * 1024 * 1024;

// The manifest note recorded on an oversized (skipped) attachment. Derived
// from GMAIL_ATTACHMENT_MAX_BYTES so it can never drift from the actual cap -
// the previous hardcoded "exceeds 25 MB" text outlived a cap change to 100 MB
// and mislabelled every skipped attachment.
export const gmailOversizeNote = (): string =>
  `not pulled - exceeds ${Math.round(GMAIL_ATTACHMENT_MAX_BYTES / (1024 * 1024))} MB`;

// ---------------------------------------------------------------------- //
// Transient-error backoff: retry rate-limit / concurrency / 5xx responses
// ---------------------------------------------------------------------- //

// Total attempts a single Gmail call makes before giving up, and the base for
// exponential backoff between them. A concurrency 429 ("too many concurrent
// requests") clears in seconds once in-flight requests drain, so short backoff
// + a few retries almost always succeeds without a hard failure.
const GMAIL_MAX_ATTEMPTS = 4;
const GMAIL_BACKOFF_BASE_MS = 500;

// Surfaced before each backoff wait so the caller can message the user
// ("Gmail busy, retrying in Ns…") instead of hard-failing.
export interface GmailRetryNotice {
  attempt: number; // the attempt that just failed (1-based)
  maxAttempts: number;
  delayMs: number; // wait before the next attempt
  status: number; // the HTTP status that triggered the retry
}
export type OnGmailRetry = (notice: GmailRetryNotice) => void;

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

// 429 (rate / concurrency) and 5xx are transient and worth retrying; 401
// (token), 403 (permission), 404 are terminal and surfaced immediately. 401 in
// particular must NOT be retried here - the caller refreshes the token + retries
// (callWith401Retry).
const isRetryableStatus = (status: number): boolean =>
  status === 429 || (status >= 500 && status < 600);

// Honour Gmail's Retry-After when present (delta-seconds or HTTP-date), else
// exponential backoff with up to +50% jitter so concurrent callers don't
// resynchronise their retries into a fresh burst.
const retryDelayMs = (resp: Response, attempt: number): number => {
  const header = resp.headers.get("Retry-After");
  if (header) {
    const secs = Number(header);
    if (Number.isFinite(secs)) return Math.max(0, secs * 1000);
    const dateMs = Date.parse(header);
    if (!Number.isNaN(dateMs)) return Math.max(0, dateMs - Date.now());
  }
  const base = GMAIL_BACKOFF_BASE_MS * 2 ** (attempt - 1);
  return Math.round(base * (1 + Math.random() * 0.5));
};

// Call a Gmail endpoint with the operator's token (GET unless `init` says
// otherwise), retrying transient rate-limit / concurrency / 5xx responses with
// backoff. Returns the final Response (ok, or the last non-ok one after
// exhausting retries) so each caller's existing `!resp.ok` throw produces the
// same error - just after we've given Gmail time to recover. ``onRetry`` fires
// before each wait.
export const gmailFetch = async (
  url: string,
  accessToken: string,
  onRetry?: OnGmailRetry,
  init?: { method: string; body: string },
): Promise<Response> => {
  const headers: Record<string, string> = { Authorization: `Bearer ${accessToken}` };
  if (init) headers["Content-Type"] = "application/json";
  const request = () => fetch(url, { headers, ...init });
  // A POST that failed with a 5xx may still have landed; repeating it could make a
  // second draft. A 429 was refused before it ran.
  const retryable = (status: number) =>
    init?.method === "POST" ? status === 429 : isRetryableStatus(status);
  let resp = await request();
  for (
    let attempt = 1;
    attempt < GMAIL_MAX_ATTEMPTS && retryable(resp.status);
    attempt++
  ) {
    const delayMs = retryDelayMs(resp, attempt);
    onRetry?.({ attempt, maxAttempts: GMAIL_MAX_ATTEMPTS, delayMs, status: resp.status });
    await sleep(delayMs);
    resp = await request();
  }
  return resp;
};

export interface GmailThreadStub {
  id: string;
  snippet: string;
}

export interface GmailThreadPreview {
  id: string;
  subject: string;
  from: string;
  date: string;
  snippet: string;
  messageCount: number;
}

// One message in a structured thread.
export interface GmailStructuredMessage {
  // The Gmail message id. Recorded so the backend extractor can map an
  // image attachment (which carries its owning `messageId`) back to the
  // message it was pasted into, and splice the OCR'd text into THAT
  // message's body in conversation context.
  message_id: string;
  sent_at: string;
  sender: string;
  to: string[];
  cc: string[];
  body: string;
}

// One attachment as discovered on a message part. This is the raw
// discovery shape (carries attachmentId + messageId so the bytes can
// be fetched); the persisted manifest entry adds s3_key + status.
export interface GmailAttachmentRef {
  filename: string;
  mime_type: string;
  size_bytes: number;
  attachmentId: string;
  messageId: string;
  // The part's Content-ID (angle brackets stripped), when present. An
  // image pasted inline carries a Content-ID that the plaintext body
  // references as a `[cid:<content_id>]` placeholder at the exact spot
  // the image sat - the backend uses this to replace that placeholder
  // with the image's OCR'd text in place. "" when the part has no
  // Content-ID (a genuine file attachment).
  content_id: string;
}

export interface GmailThreadStructured {
  thread_id: string;
  subject: string;
  messages: GmailStructuredMessage[];
  attachments: GmailAttachmentRef[];
}

interface GmailMessageHeader {
  name: string;
  value: string;
}

interface GmailMessagePart {
  mimeType?: string;
  filename?: string;
  headers?: GmailMessageHeader[];
  body?: { data?: string; size?: number; attachmentId?: string };
  parts?: GmailMessagePart[];
}

interface GmailMessagePayload extends GmailMessagePart {
  headers?: GmailMessageHeader[];
}

interface GmailMessage {
  id?: string;
  labelIds?: string[];
  payload?: GmailMessagePayload;
  snippet?: string;
}

// An unsent draft sits in its thread like any message, so once stored it reads
// as correspondence that went out. Mirrors ``is_unsent_draft`` in
// src/inrange_agents/adaptors/gmail_client.py.
export const isUnsentDraft = (msg: { labelIds?: string[] }): boolean =>
  (msg.labelIds ?? []).includes("DRAFT");

interface GmailThreadResponse {
  messages?: GmailMessage[];
}

interface GmailSearchResponse {
  threads?: { id: string; snippet?: string }[];
}

interface GmailProfileResponse {
  emailAddress?: string;
}

interface GmailAttachmentResponse {
  data?: string;
}

// ---------------------------------------------------------------------- //
// Profile - the operator's own Gmail address (`pulled_from`)
// ---------------------------------------------------------------------- //

// The signed-in mailbox's address. Recorded on the upload row as
// `pulled_from` so the Files tab can show which inbox a thread came
// from and so a future re-pull knows whose mailbox to hit.
export const getThreadProfileEmail = async (
  accessToken: string,
  onRetry?: OnGmailRetry,
): Promise<string> => {
  const resp = await gmailFetch(`${GMAIL_API_BASE}/profile`, accessToken, onRetry);
  if (!resp.ok) {
    throw new Error(
      `Gmail profile fetch failed (${resp.status}): ${await resp.text()}`,
    );
  }
  const data = (await resp.json()) as GmailProfileResponse;
  return data.emailAddress ?? "";
};

// ---------------------------------------------------------------------- //
// Search
// ---------------------------------------------------------------------- //

// Search threads matching the operator's Gmail query string. The
// query syntax mirrors Gmail's web UI - `from:`, `subject:`,
// `after:YYYY/MM/DD`, plain keywords, boolean OR. We list threads
// (not messages) so a multi-message conversation imports as one
// logical unit rather than N individually.
export const searchThreads = async (
  query: string,
  accessToken: string,
  onRetry?: OnGmailRetry,
): Promise<GmailThreadStub[]> => {
  const url = new URL(`${GMAIL_API_BASE}/threads`);
  url.searchParams.set("q", query);
  url.searchParams.set("maxResults", String(GMAIL_MAX_RESULTS));
  const resp = await gmailFetch(url.toString(), accessToken, onRetry);
  if (!resp.ok) {
    throw new Error(`Gmail search failed (${resp.status}): ${await resp.text()}`);
  }
  const data = (await resp.json()) as GmailSearchResponse;
  return (data.threads ?? []).map((t) => ({ id: t.id, snippet: t.snippet ?? "" }));
};

// ---------------------------------------------------------------------- //
// Preview - metadata-only fetch for the search results list
// ---------------------------------------------------------------------- //

// Lightweight: pull Subject/From/Date headers + first-message
// snippet without downloading the full body. The result list shows
// these; only PICKED threads pay the full-body fetch.
//
// NOTE: URLSearchParams collapses repeated keys into one which
// breaks `metadataHeaders=Subject&metadataHeaders=From` - build
// the URL manually.
export const getThreadPreview = async (
  threadId: string,
  accessToken: string,
  onRetry?: OnGmailRetry,
): Promise<GmailThreadPreview> => {
  const built =
    `${GMAIL_API_BASE}/threads/${encodeURIComponent(threadId)}` +
    `?format=metadata&metadataHeaders=Subject&metadataHeaders=From&metadataHeaders=Date`;
  const resp = await gmailFetch(built, accessToken, onRetry);
  if (!resp.ok) {
    throw new Error(
      `Gmail thread preview failed (${resp.status}): ${await resp.text()}`,
    );
  }
  const data = (await resp.json()) as GmailThreadResponse;
  const sent = (data.messages ?? []).filter((msg) => !isUnsentDraft(msg));
  const firstMsg = sent[0] ?? {};
  const headers = headerMap(firstMsg.payload?.headers ?? []);
  return {
    id: threadId,
    subject: headers.subject ?? "(no subject)",
    from: headers.from ?? "",
    date: headers.date ?? "",
    snippet: firstMsg.snippet ?? "",
    messageCount: sent.length,
  };
};

// ---------------------------------------------------------------------- //
// Full thread -> structured document
// ---------------------------------------------------------------------- //

// Fetch the full thread and render it to the structured shape the
// backend persists as the email_thread file bytes. Each message
// carries its parsed metadata (sent_at as ISO 8601, sender, to[],
// cc[]) + the decoded body; the thread carries an attachments
// manifest walked recursively across every message part with a
// non-empty filename.
//
// The caller assembles the final JSON (adding pulled_from + the
// per-attachment s3_key/status once attachments are pulled) - this
// helper only produces the Gmail-derived fields.
export const fetchThreadStructured = async (
  threadId: string,
  accessToken: string,
  onRetry?: OnGmailRetry,
): Promise<GmailThreadStructured> => {
  const url = `${GMAIL_API_BASE}/threads/${encodeURIComponent(threadId)}?format=full`;
  const resp = await gmailFetch(url, accessToken, onRetry);
  if (!resp.ok) {
    throw new Error(`Gmail thread fetch failed (${resp.status}): ${await resp.text()}`);
  }
  const data = (await resp.json()) as GmailThreadResponse;
  const listed = data.messages ?? [];
  const sent = listed.filter((msg) => !isUnsentDraft(msg));
  if (sent.length === 0) {
    throw new Error(
      listed.length === 0
        ? `Gmail thread ${threadId} has no messages.`
        : "This Gmail thread holds only an unsent draft, so there is nothing to import yet.",
    );
  }

  const messages: GmailStructuredMessage[] = [];
  const attachments: GmailAttachmentRef[] = [];
  for (const msg of sent) {
    const headers = headerMap(msg.payload?.headers ?? []);
    messages.push({
      message_id: msg.id ?? "",
      sent_at: parseDateHeader(headers.date),
      sender: headers.from ?? "",
      to: splitAddressList(headers.to),
      cc: splitAddressList(headers.cc),
      body: decodeBody(msg.payload),
    });
    collectAttachments(msg.payload, msg.id ?? "", attachments);
  }

  const firstHeaders = headerMap(sent[0].payload?.headers ?? []);
  const subject = firstHeaders.subject ?? `Thread ${threadId}`;
  return { thread_id: threadId, subject, messages, attachments };
};

// ---------------------------------------------------------------------- //
// Attachment bytes
// ---------------------------------------------------------------------- //

// Fetch one attachment's bytes by (messageId, attachmentId) and
// base64url-decode to a Blob ready to PUT to its presigned URL. The
// caller supplies the MIME type for the Blob; the Gmail attachments
// endpoint only returns the raw `data`.
export const fetchAttachmentBytes = async (
  messageId: string,
  attachmentId: string,
  accessToken: string,
  mimeType = "application/octet-stream",
  onRetry?: OnGmailRetry,
): Promise<Blob> => {
  const url =
    `${GMAIL_API_BASE}/messages/${encodeURIComponent(messageId)}` +
    `/attachments/${encodeURIComponent(attachmentId)}`;
  const resp = await gmailFetch(url, accessToken, onRetry);
  if (!resp.ok) {
    throw new Error(
      `Gmail attachment fetch failed (${resp.status}): ${await resp.text()}`,
    );
  }
  const data = (await resp.json()) as GmailAttachmentResponse;
  const bytes = b64UrlToBytes(data.data ?? "");
  // Copy into a fresh ArrayBuffer-backed view so the Blob ctor gets a
  // plain ArrayBuffer (TS otherwise widens Uint8Array's buffer to
  // ArrayBufferLike, which BlobPart rejects).
  return new Blob([bytes.slice()], { type: mimeType });
};

// ---------------------------------------------------------------------- //
// Internal: header + body + attachment decoding
// ---------------------------------------------------------------------- //

const headerMap = (headers: GmailMessageHeader[]): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const h of headers) {
    out[h.name.toLowerCase()] = h.value;
  }
  return out;
};

// Parse an RFC 2822 Date header to ISO 8601. If the value is missing
// or unparseable, keep the raw string (better a human-readable
// original than an empty field).
const parseDateHeader = (raw: string | undefined): string => {
  if (!raw) return "";
  const ms = Date.parse(raw);
  if (Number.isNaN(ms)) return raw;
  return new Date(ms).toISOString();
};

// Split a To/Cc header on commas, trim each, drop empties. Gmail
// header values can be empty / absent; return [] then.
const splitAddressList = (raw: string | undefined): string[] => {
  if (!raw) return [];
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
};

// Walk the MIME tree, return the first text/plain body; fall back
// to text/html with tags stripped. Multi-part conversations land
// here for every message in the thread.
const decodeBody = (payload: GmailMessagePart | undefined): string => {
  if (!payload) return "";
  const found: { kind: "plain" | "html"; text: string }[] = [];
  const walk = (node: GmailMessagePart | undefined): void => {
    if (!node) return;
    const mime = node.mimeType ?? "";
    if (mime === "text/plain" && node.body?.data) {
      found.push({ kind: "plain", text: b64UrlDecode(node.body.data) });
    } else if (mime === "text/html" && node.body?.data) {
      found.push({ kind: "html", text: b64UrlDecode(node.body.data) });
    }
    for (const child of node.parts ?? []) walk(child);
  };
  walk(payload);
  const plain = found.find((f) => f.kind === "plain");
  if (plain) return plain.text;
  const html = found.find((f) => f.kind === "html");
  if (html) {
    return html.text.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  }
  return "";
};

// Recursively collect every part with a non-empty `filename` into
// the attachments manifest. Each entry carries the attachmentId +
// messageId needed to fetch the bytes later, plus the size reported
// by part.body.size.
const collectAttachments = (
  payload: GmailMessagePart | undefined,
  messageId: string,
  out: GmailAttachmentRef[],
): void => {
  const walk = (node: GmailMessagePart | undefined): void => {
    if (!node) return;
    const filename = node.filename ?? "";
    const attachmentId = node.body?.attachmentId;
    if (filename && attachmentId) {
      out.push({
        filename,
        mime_type: node.mimeType ?? "application/octet-stream",
        size_bytes: node.body?.size ?? 0,
        attachmentId,
        messageId,
        content_id: contentIdOf(node),
      });
    }
    for (const child of node.parts ?? []) walk(child);
  };
  walk(payload);
};

// The part's Content-ID header, angle brackets + whitespace stripped, or
// "" when absent. An inline-pasted image carries this; the plaintext body
// references it as `[cid:<content_id>]` at the image's position.
const contentIdOf = (node: GmailMessagePart): string => {
  for (const h of node.headers ?? []) {
    if (h.name.toLowerCase() === "content-id") {
      return h.value.trim().replace(/^</, "").replace(/>$/, "").trim();
    }
  }
  return "";
};

// Gmail returns base64url-encoded body data. Convert to standard
// base64 then to a UTF-8 string. `atob` handles latin-1; round-
// trip through a Uint8Array + TextDecoder for unicode safety.
const b64UrlDecode = (s: string): string => {
  try {
    const bytes = b64UrlToBytes(s);
    return new TextDecoder("utf-8", { fatal: false }).decode(bytes);
  } catch {
    return "";
  }
};

// base64url -> raw bytes. Shared by body decode (text) + attachment
// fetch (binary). Pads to a multiple of 4 and swaps the URL-safe
// alphabet back to standard base64 before atob.
const b64UrlToBytes = (s: string): Uint8Array => {
  const standard = s.replace(/-/g, "+").replace(/_/g, "/");
  const padded = standard.padEnd(
    standard.length + ((4 - (standard.length % 4)) % 4),
    "=",
  );
  const binary = atob(padded);
  return Uint8Array.from(binary, (c) => c.charCodeAt(0));
};

// ---------------------------------------------------------------------- //
// Filename slug helper
// ---------------------------------------------------------------------- //

// Slugify a subject + append a short thread-id suffix for a unique,
// path-safe filename. Used for the thread JSON's original_name.
export const threadFileSlug = (subject: string, threadId: string): string => {
  const slug =
    (subject || "email")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 60) || "email";
  return `${slug}-${threadId.slice(0, 8)}`;
};
